// =============================================================================
// features/policies/PolicyAcceptanceGate.tsx — Trava de ciência de política
// interna (F51-S17).
//
// "Usuário sem aceite da versão vigente não abre a inbox" — este componente
// envolve a tela (`MeuWhatsAppPage`/`MeuWhatsAppInboxPage`) e substitui os
// filhos por um diálogo obrigatório enquanto `accepted !== true`. Não há
// botão de fechar/pular — a única saída é confirmar ciência.
//
// Fail-closed (nota do slot): qualquer resposta que não seja um 200 com
// `accepted: true` bloqueia — erro de rede, 404 (organização sem documento
// cadastrado) ou accepted=false tratam igual: sem inbox. Nunca "abre por
// padrão" na dúvida.
// =============================================================================
import * as React from 'react';

import { Button } from '../../components/ui/Button';
import { ApiError } from '../../lib/api';

import { useAcceptPolicy, usePolicyStatus } from './queries';

export interface PolicyAcceptanceGateProps {
  readonly policyKey: string;
  readonly children: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------

function GateLoading(): React.JSX.Element {
  return (
    <div className="flex items-center justify-center min-h-[320px]" role="status">
      <div className="w-6 h-6 rounded-full border-2 border-border-strong border-t-azul animate-spin" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fail-closed — não foi possível confirmar a ciência (rede, 404, etc.)
// ---------------------------------------------------------------------------

function GateBlockedByError({ error }: { readonly error: unknown }): React.JSX.Element {
  const notConfigured = error instanceof ApiError && error.status === 404;

  return (
    <div className="flex flex-col items-center justify-center gap-3 min-h-[320px] px-6 text-center">
      <p className="font-sans font-medium text-ink" style={{ fontSize: 'var(--text-base)' }}>
        {notConfigured
          ? 'A política de monitoramento desta organização ainda não foi cadastrada.'
          : 'Não foi possível confirmar sua ciência da política de monitoramento.'}
      </p>
      <p className="font-sans text-ink-3" style={{ fontSize: 'var(--text-sm)' }}>
        {notConfigured
          ? 'Fale com o administrador para configurar o texto antes de usar esta tela.'
          : 'Tente novamente em instantes. Se o problema continuar, fale com o administrador.'}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Diálogo obrigatório de ciência
// ---------------------------------------------------------------------------

interface AcceptanceDialogProps {
  readonly title: string;
  readonly body: string;
  readonly onAccept: () => void;
  readonly isPending: boolean;
  readonly hasError: boolean;
}

function AcceptanceDialog({
  title,
  body,
  onAccept,
  isPending,
  hasError,
}: AcceptanceDialogProps): React.JSX.Element {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[var(--text)]/60 backdrop-blur-[4px]"
      role="dialog"
      aria-modal="true"
      aria-label="Ciência de monitoramento obrigatória"
    >
      <div
        className="w-full max-w-lg rounded-md flex flex-col gap-6 p-6"
        style={{
          background: 'var(--bg-elev-1)',
          boxShadow: 'var(--elev-5)',
          border: '1px solid var(--border)',
          animation: 'fade-up 200ms var(--ease-out) both',
        }}
      >
        <div className="flex flex-col gap-1.5">
          <h2
            className="font-display font-bold text-ink"
            style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.03em' }}
          >
            {title}
          </h2>
          <p className="font-sans text-ink-2" style={{ fontSize: 'var(--text-sm)' }}>
            Leia com atenção antes de continuar. É preciso confirmar ciência para abrir esta tela.
          </p>
        </div>

        <div
          className="rounded-sm px-4 py-3 max-h-64 overflow-y-auto"
          style={{ background: 'var(--bg-elev-2)', border: '1px solid var(--border)' }}
        >
          <p
            className="font-sans whitespace-pre-wrap"
            style={{ fontSize: 'var(--text-sm)', color: 'var(--text-2)' }}
          >
            {body}
          </p>
        </div>

        {hasError && (
          <p className="font-sans" style={{ fontSize: 'var(--text-xs)', color: 'var(--danger)' }}>
            Não foi possível registrar sua confirmação. Tente novamente.
          </p>
        )}

        <Button
          variant="primary"
          onClick={onAccept}
          disabled={isPending}
          className="w-full justify-center"
        >
          {isPending ? 'Confirmando…' : 'Estou ciente'}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PolicyAcceptanceGate
// ---------------------------------------------------------------------------

export function PolicyAcceptanceGate({
  policyKey,
  children,
}: PolicyAcceptanceGateProps): React.JSX.Element {
  const status = usePolicyStatus(policyKey);
  const accept = useAcceptPolicy(policyKey);

  if (status.isLoading) return <GateLoading />;

  // Fail-closed: qualquer erro (rede, 404 sem documento cadastrado) bloqueia.
  if (status.isError || status.data === undefined) {
    return <GateBlockedByError error={status.error} />;
  }

  if (!status.data.accepted) {
    return (
      <AcceptanceDialog
        title={status.data.document.title}
        body={status.data.document.body}
        onAccept={() => accept.mutate()}
        isPending={accept.isPending}
        hasError={accept.isError}
      />
    );
  }

  return <>{children}</>;
}
